import { useNotification } from '@app/AppLayout/NotificationContext';
import {
  AlertVariant,
  Button,
  Card,
  CardBody,
  Content,
  Divider,
  Flex,
  FlexItem,
  PageSection,
  Split,
  SplitItem,
  Title
} from '@patternfly/react-core';
import { PlayIcon, UploadIcon } from '@patternfly/react-icons';
import * as React from 'react';

// Make sure these imports match your actual file structure
import { AutomationApi } from '@app/api';
import { ApiError } from '@app/utils/ApiError';
import {
  AUTOMATION_AD_HOC_EVENTS_PROCESS,
  AUTOMATION_CERTIFICATION_PROCESS,
  AUTOMATION_COURSES_PROCESS,
  AUTOMATION_INSTRUCTORS_PROCESS,
  AUTOMATION_OUTLOOK_REPORT_PROCESS,
  AUTOMATION_PIPELINE_EVENTS_PROCESS,
  AUTOMATION_PRIVATE_EVENTS_PROCESS,
  AUTOMATION_SURVEY_PROCESS
} from '@app/utils/config';
import { ProcessBodySpec, buildProcessBody } from './ProcessBody';
import { ProcessBodyModal } from './ProcessBodyModal';

interface AutomationProcess {
  id: string;
  name: string;
  description: string;
  /**
   * Set when the automation system expects content with the trigger: what to collect, and how it
   * is assembled into the body. ProcessBodyModal does the collecting, `buildProcessBody` the
   * assembling, so a process needing a different set of files is a change to this list alone.
   */
  body?: ProcessBodySpec;
}

/**
 * What to tell the user when a trigger fails.
 *
 * 429 is checked before the message because the throttle answers with an empty body, so the
 * parsed message is a generic "unknown error" that says nothing about the one thing the user
 * needs to know: that the process ran too recently and this is not a failure to fix.
 */
const reasonFor = (error: unknown): string => {
  if (error instanceof ApiError && error.code === 429) {
    return 'This process has been triggered too recently. Please try again later.';
  }
  return error instanceof Error && error.message
    ? error.message
    : 'An unexpected error occurred while communicating with the server.';
};

const AutomationProcesses: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  // Track loading state per process using the process ID as the key
  const [isProcessing, setIsProcessing] = React.useState<Record<string, boolean>>({});
  /** The process whose body is being collected, if any. Only one modal is ever open. */
  const [bodyProcess, setBodyProcess] = React.useState<AutomationProcess | null>(null);

  /** @returns whether the trigger was accepted, so the caller knows whether to keep a form open. */
  const handleTriggerProcess = async (processId: string, friendlyName: string, body?: string): Promise<boolean> => {
    setIsProcessing(prev => ({ ...prev, [processId]: true }));

    try {
      // Body-less for most processes; the ones that take content pass it straight through.
      await AutomationApi.triggerProcess(processId, body);

      addNotification(
        'Process Triggered',
        AlertVariant.success,
        `The ${friendlyName} process was successfully triggered.`
      );
      return true;
    } catch (error: unknown) {
      addNotification(
        `Error triggering ${friendlyName}`,
        AlertVariant.danger,
        reasonFor(error)
      );
      return false;
    } finally {
      setIsProcessing(prev => ({ ...prev, [processId]: false }));
    }
  };

  /**
   * Closes the modal only once the service has taken the body. A failed trigger leaves it open
   * with the content intact - a report that was just pasted in is not something to make somebody
   * find and paste again because the service was briefly busy.
   */
  const handleBodySubmit = async (process: AutomationProcess, collected: Record<string, string>) => {
    if (!process.body) {
      return;
    }
    const body = buildProcessBody(process.body, collected);
    if (await handleTriggerProcess(process.id, process.name, body)) {
      setBodyProcess(null);
    }
  };

  // Define your processes here for easy rendering and maintenance
  const processes: AutomationProcess[] = [
    {
      id: AUTOMATION_OUTLOOK_REPORT_PROCESS,
      name: 'Outlook Report processing',
      description: 'Upload and process the outlook report file generated by the LMS.',
      // One file, forwarded as it stands: this process reads the report itself, not a wrapper.
      body: {
        format: 'raw',
        parts: [{ key: 'report', label: 'Content to process' }]
      }
    },
    {
      id: AUTOMATION_COURSES_PROCESS,
      name: 'Courses Synchronization',
      description: 'Triggers the automated process to fetch and update the latest course catalog data.'
    },
    {
      id: AUTOMATION_INSTRUCTORS_PROCESS,
      name: 'Instructors Synchronization',
      description: 'Triggers the automated process to synchronize instructor profiles.'
    },
    {
      id: AUTOMATION_PRIVATE_EVENTS_PROCESS,
      name: 'Private Events Synchronization',
      description: 'Triggers the automated process to pull and update private events and their details.'
    },
    {
      id: AUTOMATION_AD_HOC_EVENTS_PROCESS,
      name: 'Ad Hoc Events Synchronization',
      description: 'Triggers the automated process to pull and update ad hoc events and their details.'
    },
    {
      id: AUTOMATION_PIPELINE_EVENTS_PROCESS,
      name: 'Pipeline Events Synchronization',
      description: 'Triggers the automated process to pull and update pipelone events and their details.'
    },
    {
      id: AUTOMATION_CERTIFICATION_PROCESS,
      name: 'Cerification Synchronization',
      description: 'Triggers the automated process to pull and update the instructors certifications from their public profile.'
    },
    {
      id: AUTOMATION_SURVEY_PROCESS,
      name: 'Survey processing',
      description: 'Upload the survey offerings and submissions exports and process them together.',
      /**
       * Two files, one trigger: this process reads them against each other, so it takes them as a
       * single JSON body - `{ offerings, submissions }` - rather than as two separate runs.
       */
      body: {
        format: 'json',
        parts: [
          { key: 'offerings', label: 'Offerings', helperText: 'The offerings export from the survey tool.' },
          { key: 'submissions', label: 'Submissions', helperText: 'The submissions export from the survey tool.' }
        ]
      }
    }
  ];

  return (
    <React.Fragment>
      <PageSection>
        <Split>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Automation Processes</Title>
          </SplitItem>
        </Split>
      </PageSection>

      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody style={{ padding: 0 }}>
            {processes.map((process, index) => (
              <React.Fragment key={process.id}>
                <Flex 
                  alignItems={{ default: 'alignItemsCenter' }} 
                  justifyContent={{ default: 'justifyContentSpaceBetween' }}
                  style={{ padding: '24px' }}
                >
                  <FlexItem>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <Content component="h3" style={{ margin: 0 }}>
                        {process.name}
                      </Content>
                      <Content component="p" style={{ color: 'var(--pf-v6-global--Color--200)', margin: 0 }}>
                        {process.description}
                      </Content>
                    </div>
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="secondary"
                      icon={process.body ? <UploadIcon /> : <PlayIcon />}
                      onClick={() => process.body
                        ? setBodyProcess(process)
                        : handleTriggerProcess(process.id, process.name)}
                      isLoading={isProcessing[process.id]}
                      isDisabled={isProcessing[process.id]}
                    >
                      {process.body ? 'Upload & Run' : 'Run Process'}
                    </Button>
                  </FlexItem>
                </Flex>
                {/* Render a divider between rows, but not after the last one */}
                {index < processes.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </CardBody>
        </Card>
      </PageSection>

      {/* Mounted only while a process is asking for one, so it opens empty every time. */}
      {bodyProcess?.body && (
        <ProcessBodyModal
          isOpen
          processName={bodyProcess.name}
          description={bodyProcess.description}
          spec={bodyProcess.body}
          isSubmitting={isProcessing[bodyProcess.id]}
          onSubmit={(collected) => void handleBodySubmit(bodyProcess, collected)}
          onClose={() => setBodyProcess(null)}
        />
      )}
    </React.Fragment>
  );
};

export { AutomationProcesses };
